import { api } from './api';
import { Resource } from '../types/resourceModel';
import {
  CreateStageOperationPayload,
  CreateStagePayload,
  NodeCanvasLayoutHint,
  OperationLibraryItem,
  OperationPositionQualificationPayload,
  OperationTypeOption,
  QualificationOption,
  ResourceNodeCleanableTargetsResponse,
  ResourceNode,
  ResourceNodeRelation,
  ResourceNodeMovePayload,
  ResourceNodePayload,
  ResourceNodeSchedulingProfilePayload,
  ResourceNodeTemplateReference,
  TemplateConstraintLink,
  TemplateResourceBindingResponse,
  TemplateResourceEditorResponse,
  TemplateResourcePlannerResponse,
  TemplateOperationResourceBinding,
  TemplateShareGroupSummary,
  TemplateSummary,
  UpdateStageOperationPayload,
  UpdateStagePayload,
} from '../components/ProcessTemplateV2/types';
import { ResourceRequirementRule } from '../components/ProcessTemplateGantt/types';

const client = api;

const mapTemplate = (data: any): TemplateSummary => ({
  id: Number(data.id),
  template_code: data.template_code,
  template_name: data.template_name,
  team_id: data.team_id ?? null,
  team_code: data.team_code ?? null,
  team_name: data.team_name ?? null,
  description: data.description ?? '',
  total_days: Number(data.total_days ?? 0),
  stage_count:
    data.stage_count !== undefined && data.stage_count !== null
      ? Number(data.stage_count)
      : data.stageCount !== undefined && data.stageCount !== null
        ? Number(data.stageCount)
        : undefined,
  unbound_count:
    data.unbound_count !== undefined && data.unbound_count !== null
      ? Number(data.unbound_count)
      : data.unboundCount !== undefined && data.unboundCount !== null
        ? Number(data.unboundCount)
        : undefined,
  constraint_conflict_count:
    data.constraint_conflict_count !== undefined && data.constraint_conflict_count !== null
      ? Number(data.constraint_conflict_count)
      : data.constraintConflictCount !== undefined && data.constraintConflictCount !== null
        ? Number(data.constraintConflictCount)
        : undefined,
  invalid_binding_count:
    data.invalid_binding_count !== undefined && data.invalid_binding_count !== null
      ? Number(data.invalid_binding_count)
      : data.invalidBindingCount !== undefined && data.invalidBindingCount !== null
        ? Number(data.invalidBindingCount)
        : undefined,
  last_validated_at: data.last_validated_at ?? data.lastValidatedAt ?? null,
  created_at: data.created_at,
  updated_at: data.updated_at,
});

const mapStage = (data: any) => ({
  id: Number(data.id),
  template_id: Number(data.template_id),
  stage_code: data.stage_code,
  stage_name: data.stage_name,
  stage_order: Number(data.stage_order),
  start_day: Number(data.start_day),
  description: data.description ?? null,
  operation_count: data.operation_count !== undefined ? Number(data.operation_count) : undefined,
});

const normalizeMetadataObject = (value: unknown): Record<string, unknown> | null => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'object') {
    return value as Record<string, unknown>;
  }
  return null;
};

const parseNodeLayoutHint = (metadata: Record<string, unknown> | null): NodeCanvasLayoutHint | null => {
  const raw = metadata?.ui_layout_v1;
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const candidate = raw as Record<string, unknown>;
  const x = Number(candidate.x);
  const y = Number(candidate.y);
  const w = Number(candidate.w);
  const h = Number(candidate.h);
  const zone = String(candidate.zone ?? '');
  const manual = candidate.manual === true;
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(w) || !Number.isFinite(h)) {
    return null;
  }
  if (!['process_floor', 'aux_lane', 'utility_lane', 'pipeline_lane'].includes(zone)) {
    return null;
  }

  const roomAnchorId =
    candidate.roomAnchorId !== undefined && candidate.roomAnchorId !== null ? Number(candidate.roomAnchorId) : undefined;
  const pinnedToNodeId =
    candidate.pinnedToNodeId !== undefined && candidate.pinnedToNodeId !== null
      ? Number(candidate.pinnedToNodeId)
      : undefined;

  return {
    x,
    y,
    w,
    h,
    zone: zone as NodeCanvasLayoutHint['zone'],
    roomAnchorId: Number.isFinite(roomAnchorId as number) ? roomAnchorId : undefined,
    pinnedToNodeId: Number.isFinite(pinnedToNodeId as number) ? pinnedToNodeId : undefined,
    manual,
  };
};

const mapResourceNode = (data: any): ResourceNode => {
  const metadata = normalizeMetadataObject(data.metadata ?? null);

  return {
    id: Number(data.id),
    nodeCode: data.nodeCode ?? data.node_code,
    nodeName: data.nodeName ?? data.node_name,
    nodeClass: data.nodeClass ?? data.node_class,
    nodeSubtype: data.nodeSubtype ?? data.node_subtype ?? null,
    parentId: data.parentId ?? data.parent_id ?? null,
    nodeScope: (data.nodeScope ?? data.node_scope ?? 'GLOBAL') as 'GLOBAL' | 'DEPARTMENT',
    departmentCode: data.departmentCode ?? data.department_code ?? null,
    equipmentSystemType: data.equipmentSystemType ?? data.equipment_system_type ?? null,
    equipmentCategoryId: data.equipmentCategoryId ?? data.equipment_category_id ?? null,
    equipmentModelId: data.equipmentModelId ?? data.equipment_model_id ?? null,
    equipmentClass: data.equipmentClass ?? data.equipment_class ?? null,
    equipmentModel: data.equipmentModel ?? data.equipment_model ?? null,
    boundResourceId: data.boundResourceId ?? data.bound_resource_id ?? null,
    boundResourceCode: data.boundResourceCode ?? data.bound_resource_code ?? null,
    boundResourceName: data.boundResourceName ?? data.bound_resource_name ?? null,
    boundResourceType: data.boundResourceType ?? data.bound_resource_type ?? null,
    boundResourceStatus: data.boundResourceStatus ?? data.bound_resource_status ?? null,
    boundResourceIsSchedulable: Boolean(data.boundResourceIsSchedulable ?? data.bound_resource_is_schedulable),
    boundResourceCapacity:
      data.boundResourceCapacity ?? data.bound_resource_capacity ?? null,
    boundResourceIsShared: Boolean(data.boundResourceIsShared ?? data.bound_resource_is_shared),
    sortOrder: Number(data.sortOrder ?? data.sort_order ?? 0),
    isActive: Boolean(data.isActive ?? data.is_active),
    metadata,
    layoutHint: parseNodeLayoutHint(metadata),
    childCount: Number(data.childCount ?? data.child_count ?? 0),
    templateBindingCount: Number(data.templateBindingCount ?? data.template_binding_count ?? 0),
    children: (data.children ?? []).map((item: any) => mapResourceNode(item)),
  };
};

const mapTemplateOperationResourceBinding = (data: any): TemplateOperationResourceBinding => ({
  id: Number(data.id),
  templateScheduleId: Number(data.templateScheduleId ?? data.template_schedule_id),
  resourceNodeId: Number(data.resourceNodeId ?? data.resource_node_id),
  bindingMode: data.bindingMode ?? data.binding_mode ?? 'DEFAULT',
  bindingRole: data.bindingRole ?? data.binding_role ?? 'PRIMARY',
  status: data.status ?? 'BOUND',
  reason: data.reason ?? null,
  node: data.node ? mapResourceNode(data.node) : null,
});

const mapPlannerOperation = (data: any) => ({
  ...data,
  id: Number(data.id),
  stage_id: Number(data.stage_id),
  operation_id: Number(data.operation_id),
  operation_day: Number(data.operation_day ?? 0),
  recommended_time: Number(data.recommended_time ?? 0),
  recommended_day_offset:
    data.recommended_day_offset !== undefined && data.recommended_day_offset !== null
      ? Number(data.recommended_day_offset)
      : undefined,
  window_start_time:
    data.window_start_time !== undefined && data.window_start_time !== null ? Number(data.window_start_time) : undefined,
  window_start_day_offset:
    data.window_start_day_offset !== undefined && data.window_start_day_offset !== null
      ? Number(data.window_start_day_offset)
      : undefined,
  window_end_time:
    data.window_end_time !== undefined && data.window_end_time !== null ? Number(data.window_end_time) : undefined,
  window_end_day_offset:
    data.window_end_day_offset !== undefined && data.window_end_day_offset !== null
      ? Number(data.window_end_day_offset)
      : undefined,
  operation_order: Number(data.operation_order ?? 0),
  standard_time: data.standard_time !== undefined && data.standard_time !== null ? Number(data.standard_time) : undefined,
  required_people: data.required_people !== undefined && data.required_people !== null ? Number(data.required_people) : undefined,
  stage_order: Number(data.stage_order ?? 0),
  stage_start_day: Number(data.stage_start_day ?? 0),
  defaultResourceNodeId: data.default_resource_node_id ?? null,
  defaultResourceNodeName: data.default_resource_node_name ?? null,
  defaultResourceId: data.default_resource_id ?? null,
  defaultResourceCode: data.default_resource_code ?? null,
  resourceBindings: (data.resource_bindings ?? data.resourceBindings ?? []).map(mapTemplateOperationResourceBinding),
  bindingStatus: data.binding_status,
  bindingReason: data.binding_reason ?? null,
});

const mapPlannerResponse = (data: any): TemplateResourcePlannerResponse => ({
  template: mapTemplate(data.template),
  stages: (data.stages ?? []).map(mapStage),
  operations: (data.operations ?? []).map(mapPlannerOperation),
  resourceTree: (data.resource_tree ?? []).map((item: any) => mapResourceNode(item)),
  metrics: {
    totalOperations: Number(data.metrics?.total_operations ?? 0),
    boundOperations: Number(data.metrics?.bound_operations ?? 0),
    unboundOperations: Number(data.metrics?.unbound_operations ?? 0),
    invalidBindings: Number(data.metrics?.invalid_bindings ?? 0),
    resourceNodeCount: Number(data.metrics?.resource_node_count ?? 0),
  },
  warnings: data.warnings ?? [],
});

const mapConstraint = (data: any): TemplateConstraintLink => ({
  constraintId: Number(data.constraintId ?? data.constraint_id),
  fromScheduleId: Number(data.fromScheduleId ?? data.from_schedule_id),
  fromOperationId: Number(data.fromOperationId ?? data.from_operation_id),
  fromOperationName: data.fromOperationName ?? data.from_operation_name,
  fromOperationCode: data.fromOperationCode ?? data.from_operation_code,
  toScheduleId: Number(data.toScheduleId ?? data.to_schedule_id),
  toOperationId: Number(data.toOperationId ?? data.to_operation_id),
  toOperationName: data.toOperationName ?? data.to_operation_name,
  toOperationCode: data.toOperationCode ?? data.to_operation_code,
  constraintType: Number(data.constraintType ?? data.constraint_type ?? 1),
  lagTime: Number(data.lagTime ?? data.lag_time ?? 0),
  lagType: data.lagType ?? data.lag_type ?? null,
  lagMin: data.lagMin !== undefined || data.lag_min !== undefined ? Number(data.lagMin ?? data.lag_min ?? 0) : null,
  lagMax: data.lagMax !== undefined || data.lag_max !== undefined ? Number(data.lagMax ?? data.lag_max) : null,
  shareMode: data.shareMode ?? data.share_mode ?? 'NONE',
  constraintLevel:
    data.constraintLevel !== undefined || data.constraint_level !== undefined
      ? Number(data.constraintLevel ?? data.constraint_level)
      : null,
  constraintName: data.constraintName ?? data.constraint_name ?? null,
  description: data.description ?? null,
  fromStageName: data.fromStageName ?? data.from_stage_name,
  toStageName: data.toStageName ?? data.to_stage_name,
  fromOperationDay: Number(data.fromOperationDay ?? data.from_operation_day ?? 0),
  fromRecommendedTime: Number(data.fromRecommendedTime ?? data.from_recommended_time ?? 0),
  fromRecommendedDayOffset:
    data.fromRecommendedDayOffset !== undefined || data.from_recommended_day_offset !== undefined
      ? Number(data.fromRecommendedDayOffset ?? data.from_recommended_day_offset ?? 0)
      : null,
  toOperationDay: Number(data.toOperationDay ?? data.to_operation_day ?? 0),
  toRecommendedTime: Number(data.toRecommendedTime ?? data.to_recommended_time ?? 0),
  toRecommendedDayOffset:
    data.toRecommendedDayOffset !== undefined || data.to_recommended_day_offset !== undefined
      ? Number(data.toRecommendedDayOffset ?? data.to_recommended_day_offset ?? 0)
      : null,
  fromStageStartDay: Number(data.fromStageStartDay ?? data.from_stage_start_day ?? 0),
  toStageStartDay: Number(data.toStageStartDay ?? data.to_stage_start_day ?? 0),
});

const mapShareGroup = (data: any): TemplateShareGroupSummary => ({
  id: Number(data.id),
  templateId: Number(data.templateId ?? data.template_id),
  groupCode: data.groupCode ?? data.group_code,
  groupName: data.groupName ?? data.group_name,
  shareMode: data.shareMode ?? data.share_mode,
  createdAt: data.createdAt ?? data.created_at,
  memberCount: Number(data.memberCount ?? data.member_count ?? 0),
  memberIds: (data.memberIds ?? data.member_ids ?? []).map((item: unknown) => Number(item)),
  members: (data.members ?? []).map((item: any) => ({
    id: Number(item.id),
    scheduleId: Number(item.scheduleId ?? item.schedule_id),
    operationName: item.operationName ?? item.operation_name,
    requiredPeople: Number(item.requiredPeople ?? item.required_people ?? 1),
    stageName: item.stageName ?? item.stage_name,
  })),
});

const mapOperationLibraryItem = (data: any): OperationLibraryItem => ({
  id: Number(data.id),
  operation_code: data.operation_code,
  operation_name: data.operation_name,
  standard_time: Number(data.standard_time ?? 0),
  required_people: Number(data.required_people ?? 1),
  description: data.description ?? null,
  operation_type_id:
    data.operation_type_id !== undefined && data.operation_type_id !== null ? Number(data.operation_type_id) : null,
  operation_type_code: data.operation_type_code ?? null,
  operation_type_name: data.operation_type_name ?? null,
  operation_type_color: data.operation_type_color ?? null,
  team_id:
    data.team_id !== undefined && data.team_id !== null ? Number(data.team_id) : null,
  team_code: data.team_code ?? null,
  team_name: data.team_name ?? null,
  qualification_count:
    data.qualification_count !== undefined && data.qualification_count !== null ? Number(data.qualification_count) : undefined,
});

const mapOperationType = (data: any): OperationTypeOption => ({
  id: Number(data.id),
  typeCode: data.type_code,
  typeName: data.type_name,
  color: data.color ?? '#1677ff',
  teamId: data.team_id !== undefined && data.team_id !== null ? Number(data.team_id) : null,
  teamCode: data.team_code ?? null,
  teamName: data.team_name ?? null,
});

const mapQualificationOption = (data: any): QualificationOption => ({
  id: Number(data.id),
  qualification_name: data.qualification_name,
});

const mapResourceEditorResponse = (data: any): TemplateResourceEditorResponse => ({
  ...mapPlannerResponse(data),
  constraints: (data.constraints ?? []).map(mapConstraint),
  shareGroups: (data.share_groups ?? []).map(mapShareGroup),
  validation: {
    summary: {
      unplacedCount: Number(data.validation?.summary?.unplaced_count ?? 0),
      invalidBindingCount: Number(data.validation?.summary?.invalid_binding_count ?? 0),
      resourceRuleMismatchCount: Number(data.validation?.summary?.resource_rule_mismatch_count ?? 0),
      constraintConflictCount: Number(data.validation?.summary?.constraint_conflict_count ?? 0),
    },
    unplacedOperationIds: (data.validation?.unplaced_operation_ids ?? []).map((item: unknown) => Number(item)),
    invalidBindings: (data.validation?.invalid_bindings ?? []).map((item: any) => ({
      scheduleId: Number(item.scheduleId ?? item.schedule_id),
      status: item.status,
      reason: item.reason ?? null,
    })),
    resourceRuleMismatchIds: (data.validation?.resource_rule_mismatch_ids ?? []).map((item: unknown) => Number(item)),
    constraintConflicts: data.validation?.constraint_conflicts ?? [],
  },
  capabilities: {
    resourceRulesEnabled: Boolean(data.capabilities?.resource_rules_enabled),
    constraintEditEnabled: Boolean(data.capabilities?.constraint_edit_enabled),
    shareGroupEnabled: Boolean(data.capabilities?.share_group_enabled),
  },
  operationLibrary: (data.operation_library ?? []).map(mapOperationLibraryItem),
});

const toResourceNodePayload = (payload: Partial<ResourceNodePayload>) => {
  const result: Record<string, unknown> = {};
  if (payload.nodeCode !== undefined) result.node_code = payload.nodeCode;
  if (payload.nodeName !== undefined) result.node_name = payload.nodeName;
  if (payload.nodeClass !== undefined) result.node_class = payload.nodeClass;
  if (payload.nodeSubtype !== undefined) result.node_subtype = payload.nodeSubtype;
  if (payload.parentId !== undefined) result.parent_id = payload.parentId;
  if (payload.nodeScope !== undefined) result.node_scope = payload.nodeScope;
  if (payload.departmentCode !== undefined) result.department_code = payload.departmentCode;
  if (payload.equipmentSystemType !== undefined) result.equipment_system_type = payload.equipmentSystemType;
  if (payload.equipmentModelId !== undefined) result.equipment_model_id = payload.equipmentModelId;
  if (payload.equipmentClass !== undefined) result.equipment_class = payload.equipmentClass;
  if (payload.equipmentModel !== undefined) result.equipment_model = payload.equipmentModel;
  if (payload.boundResourceId !== undefined) result.bound_resource_id = payload.boundResourceId;
  if (payload.sortOrder !== undefined) result.sort_order = payload.sortOrder;
  if (payload.isActive !== undefined) result.is_active = payload.isActive;
  if (payload.metadata !== undefined) result.metadata = payload.metadata;
  return result;
};

const mergeLayoutHintIntoMetadata = (
  metadata: Record<string, unknown> | null | undefined,
  layoutHint: NodeCanvasLayoutHint,
): Record<string, unknown> => {
  const base = normalizeMetadataObject(metadata) ?? {};
  return {
    ...base,
    ui_layout_v1: {
      x: layoutHint.x,
      y: layoutHint.y,
      w: layoutHint.w,
      h: layoutHint.h,
      zone: layoutHint.zone,
      manual: layoutHint.manual,
      ...(layoutHint.roomAnchorId ? { roomAnchorId: layoutHint.roomAnchorId } : {}),
      ...(layoutHint.pinnedToNodeId ? { pinnedToNodeId: layoutHint.pinnedToNodeId } : {}),
    },
  };
};

const mapResourceNodeRelation = (data: any): ResourceNodeRelation => ({
  id: Number(data.id),
  sourceNodeId: Number(data.sourceNodeId ?? data.source_node_id),
  targetNodeId: Number(data.targetNodeId ?? data.target_node_id),
  relationType: (data.relationType ?? data.relation_type ?? 'CIP_CLEANABLE') as 'CIP_CLEANABLE',
  metadata: data.metadata ?? null,
  target: mapResourceNode(data.target),
});

const mapResourceNodeCleanableTargetsResponse = (data: any): ResourceNodeCleanableTargetsResponse => ({
  sourceNodeId: Number(data.sourceNodeId ?? data.source_node_id),
  relationType: (data.relationType ?? data.relation_type ?? 'CIP_CLEANABLE') as 'CIP_CLEANABLE',
  targets: (data.targets ?? []).map((item: any) => mapResourceNodeRelation(item)),
  candidateTargets: (data.candidateTargets ?? data.candidate_targets ?? []).map((item: any) => mapResourceNode(item)),
});

const extractCollectionPayload = (data: any): any[] => {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  return [];
};

const mapResource = (data: any): Resource => ({
  id: Number(data.id),
  resourceCode: data.resourceCode ?? data.resource_code,
  resourceName: data.resourceName ?? data.resource_name,
  resourceType: data.resourceType ?? data.resource_type,
  departmentCode: data.departmentCode ?? data.department_code,
  ownerOrgUnitId: data.ownerOrgUnitId ?? data.owner_org_unit_id ?? null,
  ownerUnitName: data.ownerUnitName ?? data.owner_unit_name ?? null,
  ownerUnitCode: data.ownerUnitCode ?? data.owner_unit_code ?? null,
  status: data.status,
  capacity: Number(data.capacity ?? 1),
  location: data.location ?? null,
  cleanLevel: data.cleanLevel ?? data.clean_level ?? null,
  isShared: Boolean(data.isShared ?? data.is_shared),
  isSchedulable: Boolean(data.isSchedulable ?? data.is_schedulable),
  metadata: data.metadata ?? null,
  stats: data.stats
    ? {
        calendarCount: Number(data.stats.calendarCount ?? data.stats.calendar_count ?? 0),
        maintenanceCount: Number(data.stats.maintenanceCount ?? data.stats.maintenance_count ?? 0),
        assignmentCount: Number(data.stats.assignmentCount ?? data.stats.assignment_count ?? 0),
      }
    : undefined,
});

/* ── 资源节点 Excel 导入 ── */
export interface ResourceNodeImportRow {
  row: number;
  nodeCode: string;
  nodeName: string;
  nodeClass: string;
  parentCode: string | null;
  status: 'create' | 'skip' | 'error' | 'created' | 'skipped';
  skipReason: string | null;
  errors: string[];
}

export interface ResourceNodeImportResult {
  dry_run: boolean;
  can_import: boolean;
  summary: {
    total: number;
    toCreate?: number;
    toSkip?: number;
    errors?: number;
    created?: number;
    skipped?: number;
    failed?: number;
  };
  rows: ResourceNodeImportRow[];
}

export const processTemplateV2Api = {
  listTemplates: async (teamId?: string) => {
    const response = await client.get('/process-templates', {
      params: teamId && teamId !== 'all' ? { team_id: teamId } : undefined,
    });
    return (response.data ?? []).map(mapTemplate);
  },
  getTemplate: async (templateId: number) => {
    const response = await client.get(`/process-templates/${templateId}`);
    return mapTemplate(response.data);
  },
  createTemplate: async (payload: { templateName: string; teamId?: number | null; description?: string }) => {
    const response = await client.post('/process-templates', {
      template_name: payload.templateName,
      team_id: payload.teamId ?? null,
      description: payload.description ?? null,
    });
    return mapTemplate(response.data);
  },
  updateTemplate: async (
    templateId: number,
    payload: { templateName: string; teamId?: number | null; description?: string | null },
  ) => {
    await client.put(`/process-templates/${templateId}`, {
      template_name: payload.templateName,
      team_id: payload.teamId ?? null,
      description: payload.description ?? null,
    });
  },
  deleteTemplate: async (templateId: number) => {
    await client.delete(`/process-templates/${templateId}`);
  },
  copyTemplate: async (templateId: number, newName?: string) => {
    const response = await client.post(`/process-templates/${templateId}/copy`, {
      new_name: newName ?? null,
    });
    return {
      newTemplateId: Number(response.data.new_template_id),
      newTemplateCode: response.data.new_template_code,
    };
  },
  getPlanner: async (templateId: number) => {
    const response = await client.get(`/process-templates/${templateId}/resource-planner`);
    return mapPlannerResponse(response.data);
  },
  getResourceEditor: async (templateId: number) => {
    const response = await client.get(`/process-templates/${templateId}/resource-editor`);
    return mapResourceEditorResponse(response.data);
  },
  validateResourceEditor: async (templateId: number) => {
    const response = await client.post(`/process-templates/${templateId}/editor-validate`);
    return response.data;
  },
  createStage: async (templateId: number, payload: CreateStagePayload) => {
    const body: {
      stage_name: string;
      stage_order?: number;
      start_day?: number;
      description: string | null;
    } = {
      stage_name: payload.stageName,
      start_day: payload.startDay,
      description: payload.description ?? null,
    };
    if (payload.stageOrder !== undefined) {
      body.stage_order = payload.stageOrder;
    }
    const response = await client.post(`/process-stages/template/${templateId}`, body);
    return mapStage(response.data);
  },
  updateStage: async (stageId: number, payload: UpdateStagePayload) => {
    await client.put(`/process-stages/${stageId}`, {
      stage_name: payload.stageName,
      stage_order: payload.stageOrder,
      start_day: payload.startDay,
      description: payload.description ?? null,
    });
  },
  deleteStage: async (stageId: number) => {
    await client.delete(`/process-stages/${stageId}`);
  },
  listOperationLibrary: async (teamId?: number | null) => {
    const response = await client.get('/operations', {
      params: teamId ? { team_id: teamId } : undefined,
    });
    return (response.data ?? []).map(mapOperationLibraryItem);
  },
  getNextOperationCode: async () => {
    const response = await client.get('/operations/next-code');
    return response.data?.nextCode ?? '';
  },
  listOperationTypes: async (teamId?: number | null) => {
    const response = await client.get('/operation-types', {
      params: teamId ? { team_id: teamId } : undefined,
    });
    return (response.data ?? []).map(mapOperationType);
  },
  createOperationLibraryItem: async (payload: {
    operationName: string;
    standardTime: number;
    requiredPeople: number;
    operationTypeId?: number | null;
    description?: string;
  }) => {
    const response = await client.post('/operations', {
      operation_name: payload.operationName,
      standard_time: payload.standardTime,
      required_people: payload.requiredPeople,
      operation_type_id: payload.operationTypeId ?? null,
      description: payload.description ?? null,
    });
    return mapOperationLibraryItem(response.data);
  },
  listAvailableQualifications: async () => {
    const response = await client.get('/operation-qualifications/available');
    return (response.data ?? []).map(mapQualificationOption);
  },
  setOperationPositionQualifications: async (
    operationId: number,
    positionNumber: number,
    qualifications: OperationPositionQualificationPayload[],
  ) => {
    await client.put(`/operation-qualifications/${operationId}/position/${positionNumber}`, {
      qualifications,
    });
  },
  createStageOperation: async (stageId: number, payload: CreateStageOperationPayload) => {
    const response = await client.post(`/stage-operations/stage/${stageId}`, {
      operation_id: payload.operationId,
      operation_day: payload.operationDay,
      recommended_time: payload.recommendedTime,
      recommended_day_offset: payload.recommendedDayOffset ?? 0,
      window_start_time: payload.windowStartTime,
      window_start_day_offset: payload.windowStartDayOffset ?? 0,
      window_end_time: payload.windowEndTime,
      window_end_day_offset: payload.windowEndDayOffset ?? 0,
    });
    return Number(response.data.id);
  },
  createStageOperationFromCanvas: async (
    templateId: number,
    payload: CreateStageOperationPayload & {
      stageId: number;
      resourceNodeId?: number | null;
      absoluteStartHour?: number;
      contract?: 'v5';
      coOccupiedResourceNodeIds?: number[];
    },
  ) => {
    const response = await client.post(`/process-templates/${templateId}/stage-operations/from-canvas`, {
      stage_id: payload.stageId,
      operation_id: payload.operationId,
      resource_node_id: payload.resourceNodeId ?? null,
      operation_day: payload.operationDay,
      recommended_time: payload.recommendedTime,
      recommended_day_offset: payload.recommendedDayOffset ?? 0,
      window_start_time: payload.windowStartTime,
      window_start_day_offset: payload.windowStartDayOffset ?? 0,
      window_end_time: payload.windowEndTime,
      window_end_day_offset: payload.windowEndDayOffset ?? 0,
      absolute_start_hour: payload.absoluteStartHour,
      contract: payload.contract,
      co_occupied_resource_node_ids: payload.coOccupiedResourceNodeIds ?? [],
    });
    return Number(response.data.id);
  },
  createStageOperationWithDefinitionFromCanvas: async (
    templateId: number,
    payload: {
      stageId: number;
      resourceNodeId?: number | null;
      coOccupiedResourceNodeIds?: number[];
      operationDay: number;
      recommendedTime: number;
      recommendedDayOffset?: number;
      absoluteStartHour?: number;
      definition: {
        name: string;
        standardHours: number;
        requiredPeople: number;
        operationTypeId?: number | null;
        description?: string;
        qualificationPositions: Array<{
          positionNumber: number;
          qualifications: OperationPositionQualificationPayload[];
        }>;
      };
    },
  ): Promise<{ scheduleId: number; operationId: number }> => {
    const response = await client.post(`/process-templates/${templateId}/stage-operations/v5-with-definition`, {
      stage_id: payload.stageId,
      resource_node_id: payload.resourceNodeId ?? null,
      co_occupied_resource_node_ids: payload.coOccupiedResourceNodeIds ?? [],
      operation_day: payload.operationDay,
      recommended_time: payload.recommendedTime,
      recommended_day_offset: payload.recommendedDayOffset ?? 0,
      absolute_start_hour: payload.absoluteStartHour,
      contract: 'v5',
      operation_definition: {
        name: payload.definition.name.trim(),
        standard_hours: payload.definition.standardHours,
        required_people: payload.definition.requiredPeople,
        operation_type_id: payload.definition.operationTypeId ?? null,
        description: payload.definition.description?.trim() || null,
        qualification_positions: payload.definition.qualificationPositions.map((position) => ({
          position_number: position.positionNumber,
          qualifications: position.qualifications,
        })),
      },
    });
    return {
      scheduleId: Number(response.data.id),
      operationId: Number(response.data.operation_id),
    };
  },
  updateStageOperation: async (scheduleId: number, payload: UpdateStageOperationPayload) => {
    await client.put(`/stage-operations/${scheduleId}`, {
      operation_id: payload.operationId,
      operation_day: payload.operationDay,
      recommended_time: payload.recommendedTime,
      recommended_day_offset: payload.recommendedDayOffset,
      window_start_time: payload.windowStartTime,
      window_start_day_offset: payload.windowStartDayOffset,
      window_end_time: payload.windowEndTime,
      window_end_day_offset: payload.windowEndDayOffset,
      operation_order: payload.operationOrder,
    });
  },
  updateStageOperationTimesBatch: async (
    updates: Array<{ scheduleId: number; timing: UpdateStageOperationPayload }>,
  ) => {
    const response = await client.put('/stage-operations/batch-time', {
      operations: updates.map(({ scheduleId, timing }) => ({
        schedule_id: scheduleId,
        operation_day: timing.operationDay,
        recommended_time: timing.recommendedTime,
        recommended_day_offset: timing.recommendedDayOffset,
        window_start_time: timing.windowStartTime,
        window_start_day_offset: timing.windowStartDayOffset,
        window_end_time: timing.windowEndTime,
        window_end_day_offset: timing.windowEndDayOffset,
      })),
    });
    return response.data as { updated: number };
  },
  moveStageOperationToStage: async (scheduleId: number, targetStageId: number, targetOperationOrder?: number) => {
    await client.post(`/stage-operations/${scheduleId}/move-stage`, {
      target_stage_id: targetStageId,
      target_operation_order: targetOperationOrder,
    });
  },
  deleteStageOperation: async (scheduleId: number) => {
    await client.delete(`/stage-operations/${scheduleId}`);
  },
  listResourceNodes: async (params?: {
    departmentCode?: string;
    includeInactive?: boolean;
    tree?: boolean;
  }) => {
    const response = await client.get('/resource-nodes', {
      params: {
        department_code: params?.departmentCode,
        include_inactive: params?.includeInactive,
        tree: params?.tree,
      },
    });
    return extractCollectionPayload(response.data).map((item: any) => mapResourceNode(item));
  },
  createResourceNode: async (payload: ResourceNodePayload) => {
    const response = await client.post('/resource-nodes', toResourceNodePayload(payload));
    return Number(response.data.id);
  },
  updateResourceNode: async (nodeId: number, payload: Partial<ResourceNodePayload>) => {
    await client.patch(`/resource-nodes/${nodeId}`, toResourceNodePayload(payload));
  },
  updateResourceNodeSchedulingProfile: async (
    nodeId: number,
    payload: ResourceNodeSchedulingProfilePayload,
  ) => {
    await client.patch(`/resource-nodes/${nodeId}/scheduling-profile`, {
      status: payload.status,
      capacity: payload.capacity,
      is_shared: payload.isShared,
      is_schedulable: payload.isSchedulable,
    });
  },
  listResourceNodeTemplateReferences: async (
    nodeId: number,
  ): Promise<ResourceNodeTemplateReference[]> => {
    const response = await client.get(`/resource-nodes/${nodeId}/template-references`);
    return (response.data?.references ?? []).map((item: any) => ({
      bindingId: Number(item.binding_id),
      templateId: Number(item.template_id),
      templateCode: item.template_code,
      templateName: item.template_name,
      stageId: Number(item.stage_id),
      stageCode: item.stage_code,
      stageName: item.stage_name,
      templateScheduleId: Number(item.template_schedule_id),
      operationId: Number(item.operation_id),
      operationCode: item.operation_code,
      operationName: item.operation_name,
      bindingRole: item.binding_role,
      operationDay: Number(item.operation_day ?? 0),
      operationOrder: Number(item.operation_order ?? 0),
    }));
  },
  updateResourceNodeLayoutHint: async (
    nodeId: number,
    layoutHint: NodeCanvasLayoutHint,
    currentMetadata?: Record<string, unknown> | null,
  ) => {
    await client.patch(
      `/resource-nodes/${nodeId}`,
      toResourceNodePayload({
        metadata: mergeLayoutHintIntoMetadata(currentMetadata, layoutHint),
      }),
    );
  },
  moveResourceNode: async (nodeId: number, payload: ResourceNodeMovePayload) => {
    await client.post(`/resource-nodes/${nodeId}/move`, {
      parent_id: payload.parentId,
      sort_order: payload.sortOrder,
    });
  },
  deleteResourceNode: async (nodeId: number) => {
    await client.delete(`/resource-nodes/${nodeId}`);
  },
  getResourceNodeCleanableTargets: async (nodeId: number): Promise<ResourceNodeCleanableTargetsResponse> => {
    const response = await client.get(`/resource-nodes/${nodeId}/cleanable-targets`);
    return mapResourceNodeCleanableTargetsResponse(response.data);
  },
  updateResourceNodeCleanableTargets: async (
    nodeId: number,
    payload: {
      targetNodeIds: number[];
    },
  ): Promise<ResourceNodeCleanableTargetsResponse> => {
    const response = await client.put(`/resource-nodes/${nodeId}/cleanable-targets`, {
      target_node_ids: payload.targetNodeIds,
    });
    return mapResourceNodeCleanableTargetsResponse(response.data);
  },
  clearResourceNodeTreeForRebuild: async () => {
    await client.post('/resource-nodes/rebuild/clear', { confirm: true });
  },
  previewResourceNodeImport: async (file: File): Promise<ResourceNodeImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await client.post('/resource-nodes/import/preview', formData);
    return response.data?.data as ResourceNodeImportResult;
  },
  importResourceNodes: async (file: File): Promise<ResourceNodeImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await client.post('/resource-nodes/import', formData);
    return response.data?.data as ResourceNodeImportResult;
  },
  getTemplateScheduleBinding: async (scheduleId: number): Promise<TemplateResourceBindingResponse> => {
    const response = await client.get(`/template-stage-operations/${scheduleId}/resource-binding`);
    return {
      templateScheduleId: Number(response.data.template_schedule_id),
      binding: response.data.binding
        ? {
            id: Number(response.data.binding.id),
            templateScheduleId: Number(response.data.binding.template_schedule_id),
            resourceNodeId: Number(response.data.binding.resource_node_id),
            bindingMode: response.data.binding.binding_mode,
            status: response.data.binding.status,
            reason: response.data.binding.reason ?? null,
            node: response.data.binding.node ? mapResourceNode(response.data.binding.node) : null,
          }
        : null,
    };
  },
  updateTemplateScheduleBinding: async (scheduleId: number, resourceNodeId: number | null) => {
    const response = await client.put(`/template-stage-operations/${scheduleId}/resource-binding`, {
      resource_node_id: resourceNodeId,
    });
    return response.data;
  },
  /**
   * Replace the full co-occupied equipment-node set for one template operation.
   * anchorNodeId is the first storage row for compatibility; coOccupiedNodeIds are equally occupied nodes.
   */
  updateScheduleBindings: async (
    scheduleId: number,
    anchorNodeId: number | null,
    coOccupiedNodeIds: number[],
  ): Promise<{
    scheduleId: number;
    // NOTE: the write endpoint echoes the full updated list as rich records
    // (resource node info nested under `node`, matching listAllTemplateScheduleBindings),
    // NOT the flat `node_name` shape of listBindingsByTemplate. Callers currently ignore
    // this payload and re-fetch via listBindingsByTemplate, but the type stays honest.
    bindings: Array<{
      id: number;
      template_schedule_id: number;
      resource_node_id: number;
      binding_mode: string;
      binding_role: string;
      node: { node_name?: string; [key: string]: unknown } | null;
      status: string;
      reason: string | null;
      [key: string]: unknown;
    }>;
  }> => {
    const response = await client.put(
      `/template-stage-operations/${scheduleId}/resource-bindings`,
      {
        primary_node_id: anchorNodeId,
        co_occupied_node_ids: coOccupiedNodeIds,
      },
    );
    return response.data;
  },
  /**
   * List all resource bindings for a template in one bulk call.
   * Used by the resource Gantt view to map operations → equipment.
   */
  listBindingsByTemplate: async (templateId: number): Promise<Array<{
    template_schedule_id: number;
    resource_node_id: number;
    binding_mode: string;
    binding_role: string;
    node_name: string;
    node_class: string;
    equipment_system_type: string | null;
    equipment_class: string | null;
  }>> => {
    const response = await client.get(`/template-stage-operations/template/${templateId}/bindings`);
    return response.data ?? [];
  },
  /**
   * Batch update resource bindings for multiple schedule IDs.
   * Pass resourceNodeId=null to unbind.
   */
  batchUpdateBindings: async (
    scheduleIds: number[],
    resourceNodeId: number | null,
    bindingRole: 'PRIMARY' | 'AUXILIARY' = 'PRIMARY',
  ): Promise<{ success: number; failed: number; total: number; errors?: string[] }> => {
    const response = await client.put('/template-stage-operations/batch-binding', {
      schedule_ids: scheduleIds,
      resource_node_id: resourceNodeId,
      binding_role: bindingRole,
    });
    return response.data;
  },
  /**
   * Batch 并占 binding: apply the same occupied equipment-node set to many template operations.
   */
  batchUpdateScheduleBindings: async (
    scheduleIds: number[],
    anchorNodeId: number | null,
    coOccupiedNodeIds: number[],
  ): Promise<{ success: number; total: number }> => {
    const response = await client.put('/template-stage-operations/batch-resource-bindings', {
      schedule_ids: scheduleIds,
      primary_node_id: anchorNodeId,
      co_occupied_node_ids: coOccupiedNodeIds,
    });
    return response.data;
  },
  updateTemplateStageOperationResources: async (scheduleId: number, requirements: ResourceRequirementRule[]) => {
    const response = await client.put(`/template-stage-operations/${scheduleId}/resources`, {
      requirements: requirements.map((rule) => ({
        resource_type: rule.resource_type,
        required_count: rule.required_count,
        is_mandatory: rule.is_mandatory,
        requires_exclusive_use: rule.requires_exclusive_use,
        prep_minutes: rule.prep_minutes,
        changeover_minutes: rule.changeover_minutes,
        cleanup_minutes: rule.cleanup_minutes,
        candidate_resource_ids: rule.candidate_resource_ids,
      })),
    });
    return response.data;
  },
  getOperationConstraints: async (scheduleId: number) => {
    const response = await client.get(`/constraints/operation/${scheduleId}`);
    return response.data;
  },
  createConstraint: async (payload: Record<string, unknown>) => {
    const response = await client.post('/constraints', payload);
    return response.data;
  },
  updateConstraint: async (constraintId: number, payload: Record<string, unknown>) => {
    const response = await client.put(`/constraints/${constraintId}`, payload);
    return response.data;
  },
  deleteConstraint: async (constraintId: number) => {
    await client.delete(`/constraints/${constraintId}`);
  },
  listOperationShareGroups: async (scheduleId: number) => {
    const response = await client.get(`/share-groups/operation/${scheduleId}`);
    return (response.data ?? []).map(mapShareGroup);
  },
  createTemplateShareGroup: async (
    templateId: number,
    payload: { groupName: string; shareMode: 'SAME_TEAM' | 'DIFFERENT'; memberIds: number[] },
  ) => {
    const response = await client.post(`/share-groups/template/${templateId}`, {
      group_name: payload.groupName,
      share_mode: payload.shareMode,
      member_ids: payload.memberIds,
    });
    return response.data;
  },
  updateTemplateShareGroup: async (
    groupId: number,
    payload: { groupName?: string; shareMode?: 'SAME_TEAM' | 'DIFFERENT'; memberIds?: number[] },
  ) => {
    const response = await client.put(`/share-groups/${groupId}`, {
      group_name: payload.groupName,
      share_mode: payload.shareMode,
      member_ids: payload.memberIds,
    });
    return response.data;
  },
  assignOperationToShareGroup: async (scheduleId: number, groupId: number) => {
    const response = await client.post('/share-groups/assign', {
      schedule_id: scheduleId,
      share_group_id: groupId,
    });
    return response.data;
  },
  removeOperationFromShareGroup: async (scheduleId: number, groupId: number) => {
    await client.delete(`/share-groups/operation/${scheduleId}/group/${groupId}`);
  },
  deleteTemplateShareGroup: async (groupId: number) => {
    await client.delete(`/share-groups/${groupId}`);
  },
  listResources: async (): Promise<Resource[]> => {
    const response = await client.get('/resources');
    const payload = Array.isArray(response.data) ? response.data : response.data?.data ?? [];
    return payload.map(mapResource);
  },
};
